// components/ui/CtaButton.tsx
'use client';

import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import type { ButtonProps } from '@relume_io/relume-ui';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  Input,
  Checkbox,
  Textarea,
  Button as RelumeButton,
  SheetClose,
} from '@relume_io/relume-ui';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import Link from "next/link";
import { trackGoogleAdsContactConversion } from "@/lib/google-ads";
import TrackedPhoneLink from "@/components/ui/TrackedPhoneLink";

type Props = {
  heading?: string;
  description?: string;
  submitButton?: ButtonProps;
  ctaLabel?: string;
};

type ApiResponse = { ok: boolean; error?: string };

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export default function CtaButton({
                                    heading = 'Powiedz nam jak możemy Ci pomóc? ',
                                    submitButton = { title: 'wyślij' },
                                    ctaLabel = 'Napisz do nas',
                                  }: Props) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [acceptTerms, setAcceptTerms] = useState<boolean | 'indeterminate'>(false);
  const [formStartedAt, setFormStartedAt] = useState(() => Date.now());

  // Honeypot (bots często to wypełniają)
  const [website, setWebsite] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{ kind: 'idle' | 'success' | 'error'; message?: string }>({
    kind: 'idle',
  });

  const canSubmit = useMemo(() => {
    const nameOk = name.trim().length > 0;
    const emailOk = isValidEmail(email);
    const messageOk = message.trim().length > 0;
    const termsOk = acceptTerms === true;
    return nameOk && emailOk && messageOk && termsOk && !isSubmitting;
  }, [name, email, message, acceptTerms, isSubmitting]);

  const resetForm = () => {
    setName('');
    setEmail('');
    setMessage('');
    setAcceptTerms(false);
    setWebsite('');
    setFormStartedAt(Date.now());
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus({ kind: 'idle' });

    const nameTrimmed = name.trim();
    const emailTrimmed = email.trim();
    const messageTrimmed = message.trim();
    const termsOk = acceptTerms === true;

    if (!nameTrimmed || !emailTrimmed || !messageTrimmed || !termsOk) {
      setStatus({
        kind: 'error',
        message: 'Uzupełnij imię, e-mail, wiadomość oraz zaakceptuj regulamin.',
      });
      return;
    }

    if (!isValidEmail(emailTrimmed)) {
      setStatus({ kind: 'error', message: 'Podaj poprawny adres e-mail.' });
      return;
    }

    if (Date.now() - formStartedAt < 1500) {
      setStatus({ kind: 'error', message: 'Wypełnij formularz i spróbuj ponownie.' });
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({
          name: nameTrimmed,
          email: emailTrimmed,
          message: messageTrimmed,
          acceptTerms: true,
          website, // honeypot (ma być puste u realnych użytkowników)
          formStartedAt,
        }),
      });

      const data = (await res.json().catch(() => null)) as ApiResponse | null;

      if (!res.ok || !data?.ok) {
        setStatus({
          kind: 'error',
          message: data?.error ?? 'Nie udało się wysłać wiadomości. Spróbuj ponownie.',
        });
        return;
      }

      resetForm();
      setStatus({ kind: 'success', message: 'Wiadomość wysłana. Dziękujemy!' });
      trackGoogleAdsContactConversion();
    } catch {
      setStatus({ kind: 'error', message: 'Błąd sieci. Spróbuj ponownie za chwilę.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const TriggerButton = (
      <StyledButton type="button" aria-label={ctaLabel} suppressHydrationWarning>
      <span className="button1__icon-wrapper" aria-hidden="true">
        <svg
            viewBox="0 0 14 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="button1__icon-svg"
            width={10}
            height={10}
        >
          <path
              d="M13.376 11.552l-.264-10.44-10.44-.24.024 2.28 6.96-.048L.2 12.56l1.488 1.488 9.432-9.432-.048 6.912 2.304.024z"
              fill="currentColor"
          />
        </svg>
        <svg
            viewBox="0 0 14 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="button1__icon-svg button1__icon-svg--copy"
            width={10}
            height={10}
        >
          <path
              d="M13.376 11.552l-.264-10.44-10.44-.24.024 2.28 6.96-.048L.2 12.56l1.488 1.488 9.432-9.432-.048 6.912 2.304.024z"
              fill="currentColor"
          />
        </svg>
      </span>
        <span className="button1__label">{ctaLabel}</span>
      </StyledButton>
  );

  return (
      <Sheet>
        <SheetTrigger asChild>{TriggerButton}</SheetTrigger>

        <SheetContent className="bg-secondary border-8 border-primary rounded-l-2xl 2xl:rounded-l-3xl overflow-hidden p-0">
          <div className="relative max-h-[100dvh] overflow-y-auto px-2 pt-4">
            <SheetClose asChild>
              <button
                  type="button"
                  aria-label="Zamknij"
                  className="absolute top-[15px] right-[-7px] md:right-[-16px] md:top-[-30px] inline-flex size-10 items-center justify-center rounded-full text-primary transition-opacity hover:opacity-70"
              >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="size-6"
                    aria-hidden="true"
                >
                  <path d="M18 6 6 18" />
                  <path d="M6 6 18 18" />
                </svg>
              </button>
            </SheetClose>

            <Dialog.Title asChild>
              <VisuallyHidden>{heading}</VisuallyHidden>
            </Dialog.Title>

            <div className="mb-4 max-w-xxs pt-2 text-left md:mb-8">
              <h2 className="mb-3 text-xl font-medium text-primary md:mb-4 md:text-4xl">
                {heading}
              </h2>
            </div>

            <form className="grid grid-cols-1 gap-4" onSubmit={handleSubmit}>
              {/* Honeypot: ukryte pole. Realny user go nie zobaczy, bot często wypełni. */}
              <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: '-10000px',
                    top: 'auto',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden',
                  }}
              >
                <label htmlFor="cta-website">Website</label>
                <input
                    id="cta-website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              <div className="grid w-full grid-cols-1 items-center">
                <VisuallyHidden asChild>
                  <label htmlFor="cta-name">Imię</label>
                </VisuallyHidden>
                <Input
                    type="text"
                    id="cta-name"
                    placeholder="IMIĘ"
                    className="rounded-xl px-4 text-left text-neutral-black placeholder:text-neutral-black"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    maxLength={120}
                    autoComplete="name"
                />
              </div>

              <div className="grid w-full grid-cols-1 items-center">
                <VisuallyHidden asChild>
                  <label htmlFor="cta-email">E-mail</label>
                </VisuallyHidden>
                <Input
                    type="email"
                    id="cta-email"
                    placeholder="E-MAIL"
                    className="rounded-xl px-4 text-left text-neutral-black placeholder:text-neutral-black"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    maxLength={254}
                    autoComplete="email"
                    inputMode="email"
                />
              </div>

              <div className="grid w-full grid-cols-1 items-center">
                <VisuallyHidden asChild>
                  <label htmlFor="cta-message">Wiadomość</label>
                </VisuallyHidden>
                <Textarea
                    id="cta-message"
                    placeholder="WIADOMOŚĆ"
                    className="min-h-[11.25rem] rounded-xl px-4 text-left text-neutral-black placeholder:text-neutral-black"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    maxLength={5000}
                />
              </div>

              {status.kind !== 'idle' ? (
                  <div
                      className={[
                        'mx-1 rounded-xl px-4 py-3 text-sm',
                        status.kind === 'success'
                            ? 'bg-secondary-light text-primary'
                            : 'bg-red-500/10 text-red-600',
                      ].join(' ')}
                      role="status"
                      aria-live="polite"
                  >
                    {status.message}
                  </div>
              ) : null}

              <div className="flex items-center justify-between gap-4 pt-5 pb-2">
                <div className="flex items-center">
                  <Checkbox
                      id="cta-terms"
                      checked={acceptTerms}
                      onCheckedChange={setAcceptTerms}
                      className="mr-2 size-5 rounded-full border border-primary bg-transparent text-secondary data-[state=checked]:bg-primary data-[state=checked]:text-secondary"
                  />
                  <label htmlFor="cta-terms" className="cursor-pointer text-sm text-primary">
                    Akceptuję{' '}
                    <Link className="underline" href="/polityka-prywatnosci-i-pliki-cookies" scroll={true}>
                      politykę prywatności
                    </Link>
                  </label>
                </div>

                <RelumeButton
                    type="submit"
                    disabled={!canSubmit}
                    aria-disabled={!canSubmit}
                    className="rounded-full bg-accent hover:scale-105 transition duration-500 px-6 py-2 uppercase text-white transition-colors hover:bg-accent-dark disabled:opacity-60 disabled:hover:scale-100"
                >
                  {isSubmitting ? 'Wysyłanie...' : submitButton?.title ?? 'Wyślij'}
                </RelumeButton>
              </div>
            </form>

            <div className="mt-4 pt-4 border-t border-primary/10 px-4 text-md">
              <div className="flex flex-col gap-2 pt-2">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <TrackedPhoneLink
                      href="tel:+48732493079"
                      className="text-primary hover:text-accent transition-colors flex items-center gap-2"
                  >
                    <span className="size-1 rounded-full bg-accent" />
                    +48 732 493 079
                  </TrackedPhoneLink>
                  <TrackedPhoneLink
                      href="tel:+48732493080"
                      className="text-primary hover:text-accent transition-colors flex items-center gap-2"
                  >
                    <span className="size-1 rounded-full bg-accent" />
                    +48 732 493 080
                  </TrackedPhoneLink>
                </div>

                <a
                    href="mailto:kontakt@passworld.pl"
                    className="text-primary hover:text-accent transition-colors flex items-center gap-2"
                >
                  <span className="size-1 rounded-full bg-accent" />
                  kontakt@passworld.pl
                </a>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
  );
}

const StyledButton = styled.button`
  line-height: 1;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  background-color: #803320;
  color: #fff;
  border-radius: 9999px;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  padding-left: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background-color 0.5s, color 0.5s;

  border: none;
  cursor: pointer;
  font-family: inherit;

  .button1__icon-wrapper {
    flex-shrink: 0;
    width: 23px;
    height: 23px;
    position: relative;
    color: #233329;
    background-color: #faf6f2;
    border-radius: 50%;
    display: grid;
    place-items: center;
    overflow: hidden;
  }

  &:hover {
    background-color: #faf6f2;
    color: #233329;
  }

  &:hover .button1__icon-wrapper {
    color: #faf6f2;
    background-color: #803320;
  }

  .button1__icon-svg--copy {
    position: absolute;
    transform: translate(-150%, 150%);
  }

  &:hover .button1__icon-svg:first-child {
    transition: transform 0.3s ease-in-out;
    transform: translate(150%, -150%);
  }

  &:hover .button1__icon-svg--copy {
    transition: transform 0.3s ease-in-out 0.1s;
    transform: translate(0);
  }

  .button1__label {
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  @media (max-width: 767px) {
    gap: 0.5rem;
    padding: 0.65rem 1.15rem;
    padding-left: 14px;
    font-size: 0.875rem;

    .button1__icon-wrapper {
      width: 20px;
      height: 20px;
    }

    .button1__icon-svg {
      width: 8px;
      height: 8px;
    }
  }
`;